import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PrivacyPolicy.css';

const IconWrapper = ({ children, size = 20, style, className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor" 
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginRight: '10px', verticalAlign: 'middle', flexShrink: 0, ...style }}
    className={className}
    aria-hidden="true"
  >
    {children}
  </svg>
);

const ICONS = {
  INTRO: <IconWrapper><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></IconWrapper>,
  COLLECT: <IconWrapper><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></IconWrapper>,
  FACULTY_CV: <IconWrapper><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></IconWrapper>,
  USE_DATA: <IconWrapper><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></IconWrapper>,
  SHARING: <IconWrapper><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></IconWrapper>,
  SECURITY: <IconWrapper><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="m9 12 2 2 4-4"></path></IconWrapper>,
  RETENTION: <IconWrapper><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></IconWrapper>,
  COOKIES: <IconWrapper><circle cx="12" cy="12" r="10"></circle><path d="M12 2a10 10 0 0 0-2 19.8"></path></IconWrapper>,
  RIGHTS: <IconWrapper><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></IconWrapper>,
  UPDATES: <IconWrapper><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></IconWrapper>,
  CONTACT: <IconWrapper><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></IconWrapper>,
  ARROW_UP: <IconWrapper size={20} style={{marginRight: 0}}><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></IconWrapper>,
  SEARCH: <IconWrapper size={18} style={{marginRight: '8px'}} className="privacy-policy-page-search-icon"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></IconWrapper>
};

const officialAddress = "Mani Casadona, IT/ITES Business Hub, New Town, Rajarhat, Kolkata, West Bengal – 700161, India";
const officialPhone = "+91 90383 20103";
const officialEmail = "nutrivioheadoffice@gmail.com";
const lastUpdatedDate = "5 Jan 2026";

const rawSections = [
  {
    id: 'introduction',
    num: "01",
    title: 'Introduction & Institutional Scope',
    icon: ICONS.INTRO,
    content: `
      <p>Welcome to <strong>Nutrivio Medical Services Private Limited</strong> (&ldquo;Nutrivio&rdquo;, &ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;). Nutrivio operates as a specialized <strong>Medical Education Institutional Advisory & Professional Services Company</strong> serving educational trusts, healthcare groups, universities, Medical Colleges, and Teaching Hospitals across India.</p>
      <p>This Privacy Policy describes our governance standards regarding the collection, storage, processing, and protection of corporate institutional records and personal professional data when you utilize our advisory portals, request institutional services, or register a medical faculty profile.</p>
    `
  },
  {
    id: 'information-collected',
    num: "02",
    title: 'Information We Collect',
    icon: ICONS.COLLECT,
    content: `
      <p>We collect information strictly necessary to fulfill institutional advisory agreements and verified medical faculty placements:</p>
      <div class="privacy-policy-page-grid-cards">
        <div class="privacy-policy-page-subcard">
          <h5>Institutional & Promoter Data</h5>
          <ul>
            <li>Trust, Society, or University Name & Registered Details</li>
            <li>Authorized Contact Person Name & Official Designation</li>
            <li>Official Email Address, Mobile Number, and WhatsApp Desk</li>
            <li>Project Feasibility Details, Proposed MBBS/PG Intake, and Bed Capacity</li>
          </ul>
        </div>
        <div class="privacy-policy-page-subcard">
          <h5>Doctor & Faculty Profiles</h5>
          <ul>
            <li>Full Name, Mobile Number, WhatsApp, and Email Address</li>
            <li>Current City, State, and Preferred Placement Locations</li>
            <li>State Medical Council / National Medical Commission (NMC) Registration</li>
            <li>Academic Qualifications, Speciality, Teaching Experience, and CV</li>
          </ul>
        </div>
      </div>
    `
  },
  {
    id: 'faculty-cv-protection',
    num: "03",
    title: 'Faculty CV & Data Confidentiality',
    icon: ICONS.FACULTY_CV,
    content: `
      <div class="privacy-policy-page-callout-box">
        <p><strong>Strict Non-Public Protection:</strong> Doctor information, CVs, and uploaded qualification documents are <strong>never publicly displayed, indexed by search engines, or shared with third-party marketing brokers</strong>.</p>
      </div>
      <p>All faculty submissions are retained in a secured, encrypted backend database accessible solely by authorized Nutrivio advisory personnel for direct matching with accredited Medical Colleges and Teaching Hospitals.</p>
    `
  },
  {
    id: 'how-we-use-data',
    num: "04",
    title: 'How We Use Collected Data',
    icon: ICONS.USE_DATA,
    content: `
      <p>We process collected data exclusively for legitimate medical education advisory and recruitment objectives:</p>
      <ul>
        <li><strong>Institutional Project Execution:</strong> Evaluating project feasibility, bed strength roadmaps, and statutory documentation for New Medical Colleges and Seat Expansions.</li>
        <li><strong>Faculty Staffing Coordination:</strong> Matching qualified medical educators with verified department-wise institutional vacancies.</li>
        <li><strong>Direct Communication:</strong> Scheduling consultation discovery meetings, coordinating interviews, and delivering milestone audit updates.</li>
      </ul>
    `
  },
  {
    id: 'data-sharing',
    num: "05",
    title: 'Information Sharing & Disclosures',
    icon: ICONS.SHARING,
    content: `
      <p>Nutrivio maintains strict corporate confidentiality. We do not sell or commercialize data. Disclosures occur only:</p>
      <ul>
        <li><strong>With Verified Client Institutions:</strong> Candidate profiles are coordinated directly with governing bodies, Deans, or Directors of hiring Medical Colleges.</li>
        <li><strong>Statutory Authorities:</strong> Where disclosure is mandated by applicable statutory laws, courts, or regulatory bodies having jurisdiction.</li>
      </ul>
    `
  },
  {
    id: 'data-security',
    num: "06",
    title: 'Technical & Infrastructure Security',
    icon: ICONS.SECURITY,
    content: `
      <p>We enforce comprehensive security measures including 256-bit SSL/TLS transmission encryption, role-based database access, automated firewalls, and encrypted cloud backups to protect your institutional and professional data.</p>
    `
  },
  {
    id: 'data-retention',
    num: "07",
    title: 'Data Retention & Deletion Rights',
    icon: ICONS.RETENTION,
    content: `
      <p>Data is retained only as long as necessary to fulfill agreed institutional advisory contracts or statutory compliance obligations. Registered doctors may request the archival, correction, or permanent deletion of their profile at any time by emailing our corporate desk.</p>
    `
  },
  {
    id: 'cookies-tracking',
    num: "08",
    title: 'Cookies & Analytics Policy',
    icon: ICONS.COOKIES,
    content: `
      <p>Our website utilizes basic session cookies and aggregated analytics to monitor system performance and refine user navigation. These tools do not collect confidential medical registration numbers or promoter financial data.</p>
    `
  },
  {
    id: 'user-rights',
    num: "09",
    title: 'Your Data Protection Rights',
    icon: ICONS.RIGHTS,
    content: `
      <p>In accordance with the <em>Digital Personal Data Protection Act (DPDPA), 2023</em> and applicable Indian IT laws, you hold the right to access, rectify, withdraw consent, or request erasure of your personal data from our active rosters.</p>
    `
  },
  {
    id: 'policy-updates',
    num: "10",
    title: 'Amendments & Updates',
    icon: ICONS.UPDATES,
    content: `
      <p>Nutrivio reserves the right to revise this Privacy Policy periodically to reflect statutory amendments or operational enhancements. Updates take effect immediately upon publication on this portal.</p>
    `
  },
  {
    id: 'contact-info',
    num: "11",
    title: 'Corporate Desk & Contact Details',
    icon: ICONS.CONTACT,
    content: `
      <p>For data privacy requests or institutional compliance inquiries, please contact our corporate desk:</p>
      <p><strong>NUTRIVIO MEDICAL SERVICES PRIVATE LIMITED</strong></p>
      <address class="privacy-policy-page-address">
        <strong>Head Office:</strong><br>
        ${officialAddress}
      </address>
      <p><strong>Phone:</strong> <a href="tel:+919038320103" class="privacy-policy-page-link">${officialPhone}</a></p>
      <p><strong>WhatsApp Desk:</strong> <a href="https://wa.me/919038320103" target="_blank" rel="noopener noreferrer" class="privacy-policy-page-link">${officialPhone}</a></p>
      <p><strong>Official Email:</strong> <a href="mailto:${officialEmail}" class="privacy-policy-page-link">${officialEmail}</a></p>
      <hr class="privacy-policy-page-divider" />
      <p class="privacy-policy-page-meta-date"><em>Last Updated: <strong>${lastUpdatedDate}</strong>. Copyright &copy; 2026 Nutrivio Medical Services Private Limited. All Rights Reserved.</em></p>
    `
  }
];

const SECTIONS_DATA = rawSections.map(s => ({
  ...s,
  id: `privacy-policy-${s.id}`
}));

const PrivacyPolicy = () => {
  const [openSections, setOpenSections] = useState([]);
  const [activeTocId, setActiveTocId] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileSelectedId, setMobileSelectedId] = useState('all');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const sectionRefs = useRef({});
  const mobileNavRef = useRef(null);

  SECTIONS_DATA.forEach(section => {
    if (!sectionRefs.current[section.id]) {
      sectionRefs.current[section.id] = React.createRef();
    }
  });

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 160;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  const toggleSection = (id) => {
    setOpenSections(prev => 
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const handleTocClick = (id) => {
    if (!openSections.includes(id)) {
      setOpenSections(prev => [...prev, id]);
    }
    setActiveTocId(id);
    scrollToSection(id);
  };

  const handleMobileSelect = (id) => {
    setMobileSelectedId(id);
    setActiveTocId(id === 'all' ? SECTIONS_DATA[0].id : id);

    if (id === 'all') {
      setOpenSections([SECTIONS_DATA[0].id]);
      scrollToSection(SECTIONS_DATA[0].id);
    } else {
      setOpenSections([id]);
      scrollToSection(id);
    }
    setIsMobileNavOpen(false);
  };

  const handleScroll = useCallback(() => {
    setShowBackToTop(window.scrollY > 350);

    let currentActiveId = null;
    const headerOffset = 170;

    const sectionsInView = SECTIONS_DATA.filter(s => 
      s.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    for (let i = sectionsInView.length - 1; i >= 0; i--) {
      const section = sectionsInView[i];
      const element = document.getElementById(section.id);
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top <= headerOffset) {
          currentActiveId = section.id;
          break;
        }
      }
    }

    if (currentActiveId) {
      setActiveTocId(currentActiveId);
    }
  }, [searchTerm]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    if (SECTIONS_DATA.length > 0) {
      setOpenSections([SECTIONS_DATA[0].id]);
      setActiveTocId(SECTIONS_DATA[0].id);
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const filteredSections = SECTIONS_DATA.filter(s =>
    s.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sectionsToRender = (mobileSelectedId !== 'all')
    ? filteredSections.filter(s => s.id === mobileSelectedId)
    : filteredSections;

  return (
    <div className="privacy-policy-page-wrapper">
      
      {/* Sticky Subheader & Breadcrumbs */}
      <div className="privacy-policy-page-subheader">
        <div className="privacy-policy-page-subheader-inner">
          <nav aria-label="Breadcrumb" className="privacy-policy-page-breadcrumbs">
            <ol>
              <li><a href="/">Home</a></li>
              <li><span className="privacy-policy-page-sep">›</span><span aria-current="page">Privacy Policy</span></li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="privacy-policy-page-container">
        <div className="privacy-policy-page-layout">
          
          {/* ================= REDESIGNED EXECUTIVE LEFT SIDEBAR ================= */}
          <aside className="privacy-policy-page-sidebar">
            <div className="privacy-policy-page-toc-card">
              
              {/* Search Filter Box */}
              <div className="privacy-policy-page-search-box">
                <div className="privacy-policy-page-search-wrap">
                  {ICONS.SEARCH}
                  <input
                    type="search"
                    placeholder="Search policy sections..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="privacy-policy-page-search-input"
                    aria-label="Filter privacy sections"
                  />
                </div>
              </div>

              {/* Mobile Dropdown Selector */}
              <div className="privacy-policy-page-mobile-dropdown" ref={mobileNavRef}>
                <button
                  type="button"
                  className="privacy-policy-page-mobile-toggle-btn"
                  onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
                >
                  <span>{mobileSelectedId === 'all' ? 'All Policy Sections' : filteredSections.find(s => s.id === mobileSelectedId)?.title}</span>
                  <span className={`privacy-policy-page-select-arrow ${isMobileNavOpen ? 'open' : ''}`}>▼</span>
                </button>
                {isMobileNavOpen && (
                  <ul className="privacy-policy-page-mobile-listbox">
                    <li 
                      className={mobileSelectedId === 'all' ? 'active' : ''}
                      onClick={() => handleMobileSelect('all')}
                    >
                      All Policy Sections
                    </li>
                    {filteredSections.map(s => (
                      <li 
                        key={s.id}
                        className={mobileSelectedId === s.id ? 'active' : ''}
                        onClick={() => handleMobileSelect(s.id)}
                      >
                        {s.title}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Desktop Table of Contents */}
              <nav className="privacy-policy-page-desktop-toc">
                <h3 className="privacy-policy-page-toc-heading">Table of Contents</h3>
                <ul className="privacy-policy-page-toc-list">
                  {filteredSections.map(s => (
                    <li key={s.id} className={`privacy-policy-page-toc-item ${activeTocId === s.id ? 'active' : ''}`}>
                      <a
                        href={`#${s.id}`}
                        onClick={(e) => { e.preventDefault(); handleTocClick(s.id); }}
                        className="privacy-policy-page-toc-link"
                      >
                        <span className="privacy-policy-page-toc-num">{s.num}</span>
                        <span className="privacy-policy-page-toc-text">{s.title.replace(/^\d+\.\s*/, '')}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Trust Badge at Sidebar Bottom */}
              <div className="privacy-policy-page-sidebar-trust-box">
                <div className="privacy-policy-page-trust-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <div>
                  <strong>DPDPA 2023 Compliant</strong>
                  <p>Encrypted data protection with strict non-disclosure protocol.</p>
                </div>
              </div>

            </div>
          </aside>

          {/* ================= MAIN PRIVACY CONTENT ================= */}
          <main className="privacy-policy-page-main-content">
            {sectionsToRender.length > 0 ? (
              sectionsToRender.map(section => {
                const isOpen = openSections.includes(section.id);
                return (
                  <div 
                    key={section.id} 
                    id={section.id} 
                    className="privacy-policy-page-card"
                    ref={sectionRefs.current[section.id]}
                  >
                    <button
                      type="button"
                      className="privacy-policy-page-card-header"
                      onClick={() => toggleSection(section.id)}
                      aria-expanded={isOpen}
                    >
                      <div className="privacy-policy-page-card-title-wrap">
                        <span className="privacy-policy-page-card-icon">{section.icon}</span>
                        <h2 className="privacy-policy-page-card-title">{section.title}</h2>
                      </div>
                      <span className={`privacy-policy-page-chevron ${isOpen ? 'open' : ''}`}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </span>
                    </button>

                    {isOpen && (
                      <div className="privacy-policy-page-card-body" dangerouslySetInnerHTML={{ __html: section.content }} />
                    )}
                  </div>
                );
              })
            ) : (
              <div className="privacy-policy-page-no-results">
                <h3>No Matching Topics</h3>
                <p>No privacy topics match "<strong>{searchTerm}</strong>". Please clear your search query.</p>
              </div>
            )}
          </main>

        </div>
      </div>

      {/* Floating Back to Top */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`privacy-policy-page-back-to-top ${showBackToTop ? 'visible' : ''}`}
        aria-label="Back to top"
      >
        {ICONS.ARROW_UP}
      </button>

    </div>
  );
};

export default PrivacyPolicy;